import type { AnnouncementMessagesType } from '@store/Global/types';
import type {
  ActionElement,
  AppMessageView,
  CreateAnnouncementRequest,
  EntityLink,
  MessageAttachment,
  MessageBlock,
  MessageField,
  MessagePayload,
  MessageText,
} from '@typeDefinitions/messagePayload';
import { ConfigManager } from '@utils/ConfigManager';
import { stripMarkdown } from '@utils/messageMarkdown';

type IncomingMessage = {
  message?: string;
  payloadJson?: unknown;
  payloadVersion?: unknown;
  payload?: unknown;
  PayloadJson?: unknown;
  PayloadVersion?: unknown;
  Payload?: unknown;
};

export type AnnouncementAudienceMode =
  | 'all'
  | 'costore'
  | 'agent'
  | 'agentOwners'
  | 'specific'
  | 'people';

type PayloadAttachment = MessageAttachment;

const INLINE_IMAGE_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.gif',
  '.webp',
  '.bmp',
  '.svg',
  '.avif',
];

function pathForExtensionCheck(value: string): string {
  try {
    const nestedUrl = value.match(/[?&]url=([^&]+)/i)?.[1];
    const candidate = nestedUrl ? decodeURIComponent(nestedUrl) : value;
    const withoutHash = candidate.split('#')[0] ?? candidate;
    const withoutQuery = withoutHash.split('?')[0] ?? withoutHash;
    return decodeURIComponent(withoutQuery).toLowerCase().replace(/\/+$/, '');
  } catch {
    return value.toLowerCase();
  }
}

function looksLikeInlineImagePath(value?: string): boolean {
  if (!value) {
    return false;
  }
  const path = pathForExtensionCheck(value);
  return INLINE_IMAGE_EXTENSIONS.some((ext) => path.endsWith(ext));
}

function isUnsupportedInlineImage(value?: string): boolean {
  if (!value) {
    return false;
  }
  return /heic|heif/i.test(value);
}

function getAttachmentMimeType(item: PayloadAttachment): string {
  return item.mimeType ?? item.fileType ?? '';
}

export function canDisplayInlineImage(item: PayloadAttachment): boolean {
  const mimeType = getAttachmentMimeType(item);
  const imageLike =
    mimeType.startsWith('image/') ||
    looksLikeInlineImagePath(item.fileName) ||
    looksLikeInlineImagePath(item.title) ||
    looksLikeInlineImagePath(item.url) ||
    looksLikeInlineImagePath(item.rawUrl) ||
    looksLikeInlineImagePath(item.thumbnailUrl);
  if (!imageLike) {
    return false;
  }
  return ![
    mimeType,
    item.fileName,
    item.title,
    item.url,
    item.rawUrl,
    item.thumbnailUrl,
  ].some((value) => isUnsupportedInlineImage(value));
}

function hasList(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

function readVersion(m: IncomingMessage): number {
  const raw = m.payloadVersion ?? m.PayloadVersion ?? 0;
  const version = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(version) ? version : 0;
}

function pickString(
  record: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }
  return undefined;
}

function pickNumber(
  record: Record<string, unknown>,
  ...keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return undefined;
}

function coerceApiAction(raw: unknown): EntityLink['apiAction'] | undefined {
  if (raw == null || typeof raw !== 'object') {
    return undefined;
  }
  const record = raw as Record<string, unknown>;
  const endpoint = pickString(record, 'endpoint', 'Endpoint');
  const method = pickString(record, 'method', 'Method');
  if (!endpoint || !method) {
    return undefined;
  }
  const payload = record.payload ?? record.Payload;
  return {
    endpoint,
    method,
    ...(payload != null &&
    typeof payload === 'object' &&
    !Array.isArray(payload)
      ? { payload: payload as Record<string, unknown> }
      : {}),
  };
}

function coerceEntityLink(raw: unknown): EntityLink | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const type = pickString(record, 'type', 'Type');
  const idFromString = pickString(record, 'entityId', 'EntityId');
  const idFromNumber = pickNumber(record, 'entityId', 'EntityId');
  const entityId =
    idFromString ?? (idFromNumber != null ? String(idFromNumber) : undefined);
  if (!type || !entityId) {
    return null;
  }
  const actionUrl = pickString(record, 'actionUrl', 'ActionUrl');
  const apiAction = coerceApiAction(record.apiAction ?? record.ApiAction);
  return {
    type,
    entityId,
    ...(actionUrl ? { actionUrl } : {}),
    ...(apiAction ? { apiAction } : {}),
  };
}

function coerceAttachment(raw: unknown): PayloadAttachment | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const url = pickString(record, 'url', 'Url');
  if (!url) {
    return null;
  }
  const rawUrl = pickString(record, 'rawUrl', 'RawUrl');
  const thumbnailUrl = pickString(record, 'thumbnailUrl', 'ThumbnailUrl');
  const fileName = pickString(record, 'fileName', 'FileName');
  const mimeType = pickString(record, 'mimeType', 'MimeType');
  const fileType = pickString(record, 'fileType', 'FileType');
  const title = pickString(record, 'title', 'Title');
  const color = pickString(record, 'color', 'Color');
  const altText = pickString(record, 'altText', 'AltText');
  const fileSize = pickNumber(record, 'fileSize', 'FileSize');
  const width = pickNumber(record, 'width', 'Width');
  const height = pickNumber(record, 'height', 'Height');
  return {
    url,
    ...(rawUrl ? { rawUrl } : {}),
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(fileName ? { fileName } : {}),
    ...(mimeType ? { mimeType } : {}),
    ...(fileType ? { fileType } : {}),
    ...(fileSize != null ? { fileSize } : {}),
    ...(width != null ? { width } : {}),
    ...(height != null ? { height } : {}),
    ...(title ? { title } : {}),
    ...(color ? { color } : {}),
    ...(altText ? { altText } : {}),
  };
}

function coerceMessageText(raw: unknown): MessageText | undefined {
  if (raw == null || typeof raw !== 'object') {
    return undefined;
  }
  const record = raw as Record<string, unknown>;
  const text = pickString(record, 'text', 'Text');
  if (!text) {
    return undefined;
  }
  const type = pickString(record, 'type', 'Type');
  return {
    type: type === 'mrkdwn' ? 'mrkdwn' : 'plain_text',
    text,
  };
}

function coerceField(raw: unknown): MessageField | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const text = pickString(record, 'text', 'Text');
  if (!text) {
    return null;
  }
  return {
    type: pickString(record, 'type', 'Type') ?? 'mrkdwn',
    text,
  };
}

function coerceActionElement(raw: unknown): ActionElement | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const text = coerceMessageText(record.text ?? record.Text);
  const value = pickString(record, 'value', 'Value');
  const actionId = pickString(record, 'actionId', 'ActionId');
  if (!text || !value || !actionId) {
    return null;
  }
  return {
    type: 'button',
    text,
    value,
    actionId,
  };
}

function coerceList<T>(
  raw: unknown,
  coerce: (item: unknown) => T | null | undefined,
): T[] | undefined {
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const items = raw.map(coerce).filter((item): item is T => item != null);
  return items.length > 0 ? items : undefined;
}

function coerceBlock(raw: unknown): MessageBlock | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const type = pickString(record, 'type', 'Type');
  if (!type) {
    return null;
  }
  const url = pickString(record, 'url', 'Url');
  const rawUrl = pickString(record, 'rawUrl', 'RawUrl');
  const fileType = pickString(record, 'fileType', 'FileType');
  const width = pickNumber(record, 'width', 'Width');
  const height = pickNumber(record, 'height', 'Height');
  const altText = pickString(record, 'altText', 'AltText');
  const text = coerceMessageText(record.text ?? record.Text);
  const fields = coerceList(record.fields ?? record.Fields, coerceField);
  const elements = coerceList(
    record.elements ?? record.Elements,
    coerceMessageText,
  );
  const actionElements = coerceList(
    record.actionElements ?? record.ActionElements,
    coerceActionElement,
  );
  return {
    type: type as MessageBlock['type'],
    ...(text ? { text } : {}),
    ...(url ? { url } : {}),
    ...(rawUrl ? { rawUrl } : {}),
    ...(fileType ? { fileType } : {}),
    ...(width != null ? { width } : {}),
    ...(height != null ? { height } : {}),
    ...(altText ? { altText } : {}),
    ...(fields ? { fields } : {}),
    ...(elements ? { elements } : {}),
    ...(actionElements ? { actionElements } : {}),
  };
}

function coercePayload(raw: unknown): MessagePayload | null {
  if (raw == null || raw === '') {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      return coercePayload(JSON.parse(raw) as unknown);
    } catch {
      return null;
    }
  }
  if (typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const text = record.text ?? record.Text;
  const markdown = record.markdown ?? record.Markdown;
  const blocks = record.blocks ?? record.Blocks;
  const entityLinks =
    record.entityLinks ?? record.entity_links ?? record.EntityLinks;
  const attachments = record.attachments ?? record.Attachments;
  const coercedBlocks = Array.isArray(blocks)
    ? blocks
        .map(coerceBlock)
        .filter((block): block is MessageBlock => block != null)
    : undefined;
  const coercedAttachments = Array.isArray(attachments)
    ? attachments
        .map(coerceAttachment)
        .filter((item): item is PayloadAttachment => item != null)
    : undefined;
  const coercedEntityLinks = Array.isArray(entityLinks)
    ? entityLinks
        .map(coerceEntityLink)
        .filter((link): link is EntityLink => link != null)
    : undefined;
  if (
    typeof text !== 'string' &&
    typeof markdown !== 'string' &&
    !coercedBlocks?.length &&
    !coercedAttachments?.length
  ) {
    return null;
  }
  return {
    text: typeof text === 'string' ? text : '',
    ...(typeof markdown === 'string' ? { markdown } : {}),
    ...(coercedBlocks?.length ? { blocks: coercedBlocks } : {}),
    ...(coercedEntityLinks?.length ? { entityLinks: coercedEntityLinks } : {}),
    ...(coercedAttachments?.length ? { attachments: coercedAttachments } : {}),
  };
}

export function isRich(m: IncomingMessage): boolean {
  const raw = m.payloadJson ?? m.payload ?? m.PayloadJson ?? m.Payload;
  return readVersion(m) > 0 && coercePayload(raw) != null;
}

export function parsePayload(m: IncomingMessage): MessagePayload | null {
  if (!isRich(m)) {
    return null;
  }
  return coercePayload(
    m.payloadJson ?? m.payload ?? m.PayloadJson ?? m.Payload,
  );
}

export function getDisplayPayload(m: IncomingMessage): MessagePayload {
  const parsed = parsePayload(m);
  if (parsed) {
    return parsed;
  }
  return buildAnnouncementPayload(m.message ?? '');
}

export function getToastText(m: IncomingMessage): string {
  const payload = parsePayload(m);
  if (payload?.text) {
    return payload.text;
  }
  return m.message ?? '';
}

export function orderNoticeLink(
  m: IncomingMessage,
): { href: string; linkText: string } | undefined {
  const payload = parsePayload(m);
  const linkedId = (payload?.entityLinks ?? [])
    .find((link) => link.type === 'Order')
    ?.entityId.trim();
  const textId = getToastText(m).match(/Order:\s*(\d+)/i)?.[1];
  const orderId = linkedId && /^\d+$/.test(linkedId) ? linkedId : textId;
  if (!orderId) {
    return undefined;
  }
  return {
    href: `/order/details/${orderId}`,
    linkText: orderId,
  };
}

export function pickEntityLink(
  payload: MessagePayload,
): EntityLink | undefined {
  const links = payload.entityLinks ?? [];
  return (
    links.find((l) => l.type === 'Order' && l.actionUrl) ??
    links.find((l) => l.actionUrl)
  );
}

function isNavigablePath(value: string): boolean {
  return value.startsWith('/') || /^https?:\/\//i.test(value);
}

export function resolveActionElement(
  el: ActionElement,
  payload: MessagePayload,
): EntityLink | undefined {
  const links = payload.entityLinks ?? [];
  const value = el.value.trim();
  const actionId = el.actionId.trim();

  if (value && isNavigablePath(value)) {
    return {
      type: actionId || 'link',
      entityId: value,
      actionUrl: value,
    };
  }

  if (value) {
    const byValue = links.find(
      (link) => link.entityId === value && Boolean(link.actionUrl),
    );
    if (byValue) {
      return byValue;
    }
  }

  const idMatch = actionId.toLowerCase();
  const byActionId = links.find((link) => {
    if (!link.actionUrl) {
      return false;
    }
    const type = link.type.toLowerCase();
    return (
      type === idMatch || `${type}:${link.entityId}`.toLowerCase() === idMatch
    );
  });
  if (byActionId) {
    return byActionId;
  }

  const navigable = links.filter((link) => link.actionUrl);
  return navigable.length === 1 ? navigable[0] : undefined;
}

export function toServeDocumentUrl(s3Key: string): string {
  const trimmed = s3Key.trim();
  if (
    trimmed.includes('ServeDocument') ||
    trimmed.startsWith('/api/') ||
    /^https?:\/\//i.test(trimmed)
  ) {
    return trimmed;
  }
  return `/api/document/ServeDocument/?url=${encodeURIComponent(trimmed)}`;
}

export function resolveServeDocumentSrc(url: string): string {
  if (!url) {
    return url;
  }
  if (/^(?:https?:\/\/|data:|blob:)/i.test(url)) {
    return url;
  }
  const path = url.startsWith('/api/') ? url : toServeDocumentUrl(url);
  const api = ConfigManager.apiUrl.replace(/\/$/, '');
  return api ? `${api}${path.startsWith('/') ? path : `/${path}`}` : path;
}

export function buildAnnouncementPayload(
  markdown: string,
  extras?: { attachments?: PayloadAttachment[] },
): MessagePayload {
  const trimmed = markdown.trim();
  const text = stripMarkdown(trimmed).slice(0, 120);
  const withUrl = (extras?.attachments ?? []).filter((item) => item.url);
  const section = trimmed
    ? [
        {
          type: 'section' as const,
          text: { type: 'mrkdwn' as const, text: trimmed },
        },
      ]
    : [];
  return {
    text,
    ...(trimmed ? { markdown: trimmed } : {}),
    ...(section.length > 0 ? { blocks: section } : {}),
    ...(withUrl.length > 0 ? { attachments: withUrl } : {}),
  };
}

export function audienceToRequestFields(
  mode: AnnouncementAudienceMode,
  officeIds: number[] = [],
  userIds: number[] = [],
): Pick<CreateAnnouncementRequest, 'officeFilter' | 'officeIds' | 'userIds'> {
  switch (mode) {
    case 'costore':
      return { officeFilter: 'CoStore' };
    case 'agent':
      return { officeFilter: 'Agent' };
    case 'agentOwners':
      return { officeFilter: 'AgentOfficeOwners' };
    case 'specific':
      return { officeIds };
    case 'people':
      return { userIds };
    case 'all':
    default:
      return {};
  }
}

export function composerSeesAnnouncement(
  mode: AnnouncementAudienceMode,
  userIds: number[],
  currentUserId: number,
): boolean {
  if (mode !== 'people') {
    return true;
  }
  return userIds.includes(currentUserId);
}

export function announcementFeedId(
  mode: AnnouncementAudienceMode,
  officeCount: number,
  userCount: number,
  createdId: number,
): number | null {
  if (mode === 'all' || (mode === 'specific' && officeCount === 1)) {
    return createdId;
  }
  if (mode === 'people' && userCount === 1) {
    return createdId;
  }
  return null;
}

export function normalizeListMessage<T extends IncomingMessage>(
  row: T,
): T & Pick<AppMessageView, 'payloadJson' | 'payloadVersion'> {
  const parsed = coercePayload(
    row.payloadJson ?? row.payload ?? row.PayloadJson ?? row.Payload,
  );
  const version = readVersion(row);
  const payloadVersion =
    version > 0
      ? version
      : parsed != null &&
          (Boolean(parsed.markdown) ||
            hasList(parsed.blocks) ||
            hasList(parsed.attachments))
        ? 1
        : 0;
  return {
    ...row,
    payloadJson: parsed ? JSON.stringify(parsed) : undefined,
    payloadVersion,
  };
}

const TEMP_ROW_ID = 1_000_000_000_000;

function isTempRowId(id: number): boolean {
  return id >= TEMP_ROW_ID;
}

function findTempDonorIndex(
  prev: AnnouncementMessagesType[],
  row: AnnouncementMessagesType,
): number {
  if (isTempRowId(row.id)) {
    return -1;
  }
  const candidates = prev
    .map((item, index) => ({ item, index }))
    .filter(
      ({ item }) =>
        isTempRowId(item.id) &&
        item.message === row.message &&
        item.createdBy === row.createdBy,
    );
  if (candidates.length === 0) {
    return -1;
  }
  if (candidates.length === 1) {
    return candidates[0].index;
  }
  const incomingTs = Date.parse(row.createdDate);
  const scored = candidates.map((candidate) => ({
    ...candidate,
    delta: Math.abs(Date.parse(candidate.item.createdDate) - incomingTs),
  }));
  scored.sort((a, b) => a.delta - b.delta);
  return scored[0].index;
}

export function mergeAnnouncementRow(
  prev: AnnouncementMessagesType[],
  incoming: AnnouncementMessagesType,
): AnnouncementMessagesType[] {
  const row = normalizeListMessage(incoming);
  const incomingParsed = parsePayload(row);
  const payloadJson =
    typeof row.payloadJson === 'string' ? row.payloadJson : undefined;

  const sameId = prev.findIndex((item) => item.id === row.id);
  if (sameId >= 0) {
    const existing = prev[sameId];
    const merged: AnnouncementMessagesType = {
      ...existing,
      ...row,
      read: existing.read || row.read,
      payloadJson: incomingParsed ? payloadJson : existing.payloadJson,
      payloadVersion: Math.max(row.payloadVersion ?? 0, readVersion(existing)),
    };
    return prev.map((item, index) => (index === sameId ? merged : item));
  }

  const matchIdx = findTempDonorIndex(prev, row);
  if (matchIdx >= 0) {
    const existing = prev[matchIdx];
    const merged: AnnouncementMessagesType = {
      ...existing,
      ...row,
      id: row.id,
      read: existing.read || row.read,
      payloadJson: incomingParsed ? payloadJson : existing.payloadJson,
      payloadVersion: Math.max(row.payloadVersion ?? 0, readVersion(existing)),
    };
    return prev.map((item, index) => (index === matchIdx ? merged : item));
  }

  return [row, ...prev];
}
